import { rentalscapeStats } from '@/data/data-selectors';
import { ERROR_TYPES } from '@/error-boundaries/errors';
import { AdminDashboard } from '@/user/user-management/admin-dashboard';
import * as userSelectors from '@/user/user-selectors';
import { toaster } from '@/utils/toaster';
import { BinaryRenderer } from '@/widgets/components/binary-renderer';
import { Input } from '@/widgets/components/input';
import { InvisibleButton } from '@/widgets/components/invisible-button';
import { MappedAlerts, SUCCESS_TYPES } from '@/widgets/mapped-alerts';
import Auth from '@aws-amplify/auth';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Tooltip, Whisper } from 'rsuite';
import {
  Avatar,
  BigMarginDiv,
  ButtonTabsWrapper,
  CenteredContainer,
  FormWrapper,
  Label,
  SmallMarginLabel,
  StyledButton,
  TabItem,
} from './user-profile-styles';

const MIN_PASSWORD_LEN = 8;
const NEW_PASS_ERROR_MESSAGE = 'New Password does not match the requirements';
const ERROR_DURATION = 5000;

async function changePassword(oldPassword, newPassword, resetFormCB) {
  try {
    const user = await Auth.currentAuthenticatedUser();
    await Auth.changePassword(user, oldPassword, newPassword); // TODO: Do we have to catch this?
    resetFormCB();
    MappedAlerts.success(SUCCESS_TYPES.PASSWORD_CHANGE);
  } catch (err) {
    if (err.code === ERROR_TYPES.InvalidPasswordException) {
      toaster.error(NEW_PASS_ERROR_MESSAGE, ERROR_DURATION);
    } else {
      MappedAlerts.error(err.code);
    }
  }
}

/**
 * Form for setting new password
 *
 * @param {*} props
 * @param {string} currPwdValue current password input field value
 * @param {string} newPwdValue the value for the new password field
 * @param {func} setCurrPwdValue callback for updating currPwdValue
 * @param {func} setNewPwdValue callback for updating newPwdValue
 * @param {func} props.resetFormCB callback for resetting form on success
 */
function NewPasswordForm(props) {
  const {
    currPwdValue,
    setCurrPwdValue,
    newPwdValue,
    setNewPwdValue,
    resetFormCB,
  } = props;
  const whisperMessage =
    'Minimum 8 characters, must include numbers, lowercase letters, uppercase letters and a special character from this set: ^ $ * . [ ] { } ( ) ? “ ! @ # % & / \\ , > < ‘ : ; | _ ~ ‘';
  const saveDisabled =
    !currPwdValue || !newPwdValue || !(newPwdValue.length >= MIN_PASSWORD_LEN);

  return (
    <FormWrapper>
      <Label>Current Password</Label>
      <Input type="password" value={currPwdValue} onChange={setCurrPwdValue} />
      <SmallMarginLabel>New Password</SmallMarginLabel>
      <Whisper
        trigger="focus"
        speaker={<Tooltip>{whisperMessage}</Tooltip>}
        disabled={!whisperMessage}
      >
        <Input type="password" value={newPwdValue} onChange={setNewPwdValue} />
      </Whisper>
      <BigMarginDiv>
        <InvisibleButton>Forgot Password</InvisibleButton>
        <StyledButton
          disabled={saveDisabled}
          onClick={() => changePassword(currPwdValue, newPwdValue, resetFormCB)}
        >
          Save Changes
        </StyledButton>
      </BigMarginDiv>
    </FormWrapper>
  );
}

const tabs = {
  PASSWORD: 'password',
  ADMIN: 'admin',
};

const formComponents = {
  [tabs.PASSWORD]: NewPasswordForm,
  [tabs.ADMIN]: AdminDashboard,
};

export function UserProfileForm(props) {
  const { displayCitySelection } = props;

  const isAdmin = useSelector(userSelectors.isAdmin);
  const [currentTab, setCurrentTab] = useState(tabs.PASSWORD); // TODO: debug.. use username
  const [currPwdValue, setCurrPwdValue] = useState('');
  const [newPwdValue, setNewPwdValue] = useState('');

  const Form = formComponents[currentTab];
  const rentalscapeStatsObj = useSelector(rentalscapeStats);

  // Passing all values and callbacks in props. Form component will use the ones that apply
  const formProps = {
    currPwdValue,
    newPwdValue,
    // must add anonymous func wrapper to setters to circumvent react 2nd callback warning
    setCurrPwdValue: (value) => setCurrPwdValue(value),
    setNewPwdValue: (value) => setNewPwdValue(value),
    resetFormCB: () => {
      setCurrPwdValue('');
      setNewPwdValue('');
    },
  };

  return (
    <CenteredContainer>
      <Avatar src={rentalscapeStatsObj?.logo} />
      <ButtonTabsWrapper displayCitySelection={displayCitySelection}>
        <TabItem
          onClick={() => setCurrentTab(tabs.PASSWORD)}
          disabled={currentTab === tabs.PASSWORD}
        >
          PASSWORD
        </TabItem>
        {isAdmin && (
          <BinaryRenderer checkpoint={displayCitySelection}>
            <TabItem
              data-cy="profile-admin-tab"
              onClick={() => setCurrentTab(tabs.ADMIN)}
              disabled={currentTab === tabs.ADMIN}
            >
              ADMIN
            </TabItem>
          </BinaryRenderer>
        )}
      </ButtonTabsWrapper>
      <Form {...formProps} />
    </CenteredContainer>
  );
}
